import React, { useState, useRef, useEffect } from 'react';

interface OTPVerificationFormProps {
  email: string;
  onSuccess: () => void;
  onBack?: () => void;
  verifyOTP: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  onResend: () => Promise<{ success: boolean; message?: string; error?: string }>;
}

export const OTPVerificationForm: React.FC<OTPVerificationFormProps> = ({
  email,
  onSuccess,
  onBack,
  verifyOTP,
  onResend
}) => {
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Setup resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (value && index === 5 && newOtpCode.every(digit => digit)) {
      handleVerify(newOtpCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handlePaste();
    }
  };

  const handlePaste = async (e?: React.ClipboardEvent) => {
    try {
      const text = e ? e.clipboardData.getData('text') : await navigator.clipboard.readText();
      const digits = text.replace(/\D/g, '').slice(0, 6);

      if (digits.length === 6) {
        const newOtpCode = digits.split('');
        setOtpCode(newOtpCode);
        inputRefs.current[5]?.focus();

        // Auto-submit
        handleVerify(digits);
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
    }
  };

  const handleVerify = async (code?: string) => {
    const fullCode = code || otpCode.join('');

    if (fullCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await verifyOTP(email, fullCode);

      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Invalid verification code');
        // Clear the code on error
        setOtpCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    try {
      setError(null);
      setResendCooldown(60); // 60 second cooldown

      const result = await onResend();

      if (!result.success) {
        setError(result.error || result.message || 'Failed to resend code');
        setResendCooldown(0);
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError('Failed to resend code. Please try again.');
      setResendCooldown(0);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {onBack && (
        <div className="mb-4">
          <button
            onClick={onBack}
            className="flex items-center text-sm text-neutral-medium-gray hover:text-neutral-black dark:hover:text-neutral-white transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      )}

      <h2 className="text-h2 font-semibold text-center text-neutral-black dark:text-neutral-white mb-2 tracking-subheading">
        Enter Verification Code
      </h2>
      <p className="text-center text-body text-neutral-medium-gray dark:text-neutral-medium-gray">
        We sent a 6-digit code to
      </p>
      <p className="text-center font-medium text-neutral-black dark:text-neutral-white mb-6">
        {email}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex justify-center gap-2">
          {otpCode.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={loading}
              className="w-12 h-14 text-center text-2xl font-bold border-2 border-neutral-medium-gray/30 dark:border-neutral-dark-gray rounded-lg bg-neutral-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 focus:outline-none disabled:bg-neutral-light-gray dark:disabled:bg-neutral-dark-gray disabled:cursor-not-allowed transition-colors"
            />
          ))}
        </div>

        <button
          onClick={() => handleVerify()}
          disabled={loading || otpCode.some(digit => !digit)}
          className="w-full flex items-center justify-center px-4 py-3 bg-brand-teal text-neutral-white font-medium rounded-lg hover:bg-brand-teal-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal disabled:bg-neutral-medium-gray disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
        >
          {loading ? (
            <>
              <img src="/images/logos/loader-circle.svg" alt="Loading..." className="-ml-1 mr-2 h-5 w-5" />
              Verifying...
            </>
          ) : (
            <>
              Verify Code
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </>
          )}
        </button>

        <p className="text-center text-sm text-neutral-medium-gray dark:text-neutral-medium-gray">
          Didn't receive the code?{' '}
          {resendCooldown > 0 ? (
            <span className="text-neutral-medium-gray/60">
              Resend in {resendCooldown}s
            </span>
          ) : (
            <button
              onClick={handleResend}
              className="text-brand-teal hover:text-brand-teal-hover hover:underline font-medium"
            >
              Resend Code
            </button>
          )}
        </p>
      </div>

      <p className="text-center text-xs text-neutral-medium-gray dark:text-neutral-medium-gray mt-6">
        The code expires in 10 minutes
      </p>
    </div>
  );
};
